import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, UserPlus, Car, Plus, Ban, X, ScanLine, Keyboard, Wrench } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { VehicleFields, EMPTY_VEHICLE } from './VehicleFields';
import { ScanChoice } from './NewClientSection';

export const clientLabel = (c) => (c.client_type === 'entreprise' && c.company_name) ? c.company_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();

export const useClientSearch = (q, onSettled) => {
  const [results, setResults] = useState([]);
  useEffect(() => {
    let alive = true;
    onSettled?.('');
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const done = (list) => { if (alive) { setResults(list); onSettled?.(q); } };
    const t = setTimeout(() => axios.get(`${API}/agenda/client-search`, { params: { q } }).then(r => done(r.data)).catch(() => done([])), 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return results;
};

const VehiclePicker = ({ client, vehicle, setVehicle }) => {
  const [scan, setScan] = useState(false);
  const onScanned = ({ vehicle: v }) => { setScan(false); setVehicle({ mode: 'new', data: v, scanned: true }); };
  return (
  <div className="space-y-2" data-testid="booking-vehicle-picker">
    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
      {client.vehicles.length ? `Sélectionnez le véhicule (${client.vehicles.length} enregistré${client.vehicles.length > 1 ? 's' : ''})` : 'Véhicule'}</p>
    {client.vehicles.length === 0 && (
      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="booking-no-vehicles-note">
        Aucun véhicule enregistré pour ce client. Scannez la carte grise ou ajoutez-le à la main : il sera mémorisé et proposé aux prochains rendez-vous.</p>
    )}
    {client.vehicles.map(v => {
      const sel = vehicle.mode === 'existing' && vehicle.id === v.id;
      return (
      <button key={v.id} type="button" onClick={() => { setScan(false); setVehicle({ mode: 'existing', id: v.id, label: `${v.brand} ${v.model} · ${v.plate}` }); }} data-testid={`booking-vehicle-${v.id}`}
        className={`w-full flex items-center gap-3 text-left text-sm px-3 py-2.5 rounded-lg border-2 transition-colors ${sel ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-400'}`}>
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${sel ? 'border-emerald-600' : 'border-slate-300'}`}>{sel && <span className="w-2 h-2 rounded-full bg-emerald-600" />}</span>
        <Car className="w-4 h-4 text-slate-500" /><span className="font-medium">{v.brand} {v.model}</span><span className="font-mono text-xs ml-auto">{v.plate}</span>
      </button>
      );
    })}
    {client.vehicles.length > 0 && <p className="text-xs text-slate-500 pt-1">Autre véhicule :</p>}
    <div className="flex flex-wrap gap-2">
      <Button type="button" size="sm" variant={scan ? 'default' : 'outline'} onClick={() => setScan(s => !s)} data-testid="booking-scan-vehicle-btn"><ScanLine className="w-3.5 h-3.5 mr-1" />Scanner la carte grise</Button>
      <Button type="button" size="sm" variant={vehicle.mode === 'new' && !vehicle.scanned ? 'default' : 'outline'} onClick={() => { setScan(false); setVehicle({ mode: 'new', data: { ...EMPTY_VEHICLE } }); }} data-testid="booking-add-vehicle-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter un véhicule à la main</Button>
      <Button type="button" size="sm" variant={vehicle.mode === 'none' ? 'default' : 'outline'} onClick={() => { setScan(false); setVehicle({ mode: 'none' }); }} data-testid="booking-no-vehicle-btn"><Ban className="w-3.5 h-3.5 mr-1" />Sans véhicule</Button>
    </div>
    {scan && <ScanChoice onScanned={onScanned} />}
    {vehicle.mode === 'new' && <VehicleFields value={vehicle.data} onChange={(data) => setVehicle({ ...vehicle, data })} />}
  </div>
  );
};

export const ClientSection = ({ client, setClient, vehicle, setVehicle, onCreateNew }) => {
  const [q, setQ] = useState('');
  const [settled, setSettled] = useState('');
  const results = useClientSearch(q, setSettled);
  const noMatch = q.trim().length >= 2 && settled === q && results.length === 0;

  if (client) {
    return (
      <section className="space-y-3" data-testid="booking-client-selected">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border">
          <div className="flex-1">
            <p className="font-semibold" data-testid="booking-client-name">{clientLabel(client)}{client.is_internal && <span className="ml-2 text-[11px] font-bold uppercase bg-[#FACC15] text-[#0E1116] px-2 py-0.5 rounded-full" data-testid="booking-internal-badge">Garage · interne</span>}</p>
            <p className="text-xs text-slate-500">{[client.phone, client.email, client.city].filter(Boolean).join(' · ')}</p>
            {client.id && (
              <button type="button" className="mt-1 text-xs font-semibold underline text-slate-600 hover:text-slate-900" data-testid="booking-toggle-internal"
                onClick={async () => {
                  try { const r = await axios.put(`${API}/agenda/clients/${client.id}/internal`, { is_internal: !client.is_internal }); setClient({ ...client, is_internal: r.data.is_internal }); toast.success(r.data.is_internal ? 'Société du garage : tâches internes proposées' : 'Client normal'); }
                  catch { toast.error('Erreur'); }
                }}>{client.is_internal ? 'Ce n\'est pas une société du garage' : 'C\'est une société du garage (tâches internes : préparation VO, expertise, nettoyage…)'}</button>
            )}
          </div>
          <button type="button" onClick={() => { setClient(null); setVehicle({ mode: 'none' }); }} className="text-slate-400 hover:text-slate-900" data-testid="booking-client-clear"><X className="w-4 h-4" /></button>
        </div>
        <VehiclePicker client={client} vehicle={vehicle} setVehicle={setVehicle} />
      </section>
    );
  }
  return (
    <section className="space-y-2" data-testid="booking-client-section">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} className="pl-9" placeholder="Rechercher un client : nom, prénom, téléphone ou plaque…" autoFocus data-testid="booking-client-search" />
      </div>
      {results.map(c => (
        <button key={c.id} type="button" data-testid={`booking-client-result-${c.id}`}
          onClick={() => { setClient(c); setVehicle(c.vehicles.length === 1 ? { mode: 'existing', id: c.vehicles[0].id, label: `${c.vehicles[0].brand} ${c.vehicles[0].model} · ${c.vehicles[0].plate}` } : { mode: 'none' }); }}
          className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-900 transition-colors">
          <p className="text-sm font-semibold">{clientLabel(c)}</p>
          <p className="text-xs text-slate-500">{[c.phone, c.city, c.vehicles.map(v => v.plate).join(', ')].filter(Boolean).join(' · ')}</p>
        </button>
      ))}
      {noMatch && <p className="text-sm text-slate-600 px-1" data-testid="booking-client-none">Aucun client trouvé pour « {q.trim()} » : créez-le ci-dessous.</p>}
      <div className="rounded-xl border-2 border-dashed border-slate-300 p-3 space-y-2" data-testid="booking-new-client-box">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" />Pas encore client ?</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Button type="button" className="h-11 bg-[#0E1116] text-[#FACC15] hover:bg-black" onClick={() => onCreateNew(q.trim(), 'choice')} data-testid="booking-create-scan-btn"><ScanLine className="w-4 h-4 mr-2" />Scanner la carte grise</Button>
          <Button type="button" variant="outline" className="h-11" onClick={() => onCreateNew(q.trim(), 'form')} data-testid="booking-create-client-btn"><Keyboard className="w-4 h-4 mr-2" />{noMatch ? `Créer « ${q.trim()} » à la main` : 'Saisie manuelle (au téléphone)'}</Button>
        </div>
      </div>
      <Button type="button" variant="outline" className="w-full h-11 border-2 border-dashed" onClick={async () => {
        try { const r = await axios.get(`${API}/agenda/internal-client`); setClient({ vehicles: [], ...r.data }); setVehicle({ mode: 'none' }); }
        catch { toast.error('Impossible de charger la fiche du garage'); }
      }} data-testid="booking-internal-btn"><Wrench className="w-4 h-4 mr-2" />Activité interne (rangement, nettoyage…) pour un collaborateur ou l'atelier</Button>
    </section>
  );
};
